import { useEffect, useState } from "react";
import { fmtEUR, fmtPct } from "@/lib/format";
import { Link } from "react-router-dom";
import { Trash2, Eye, Calendar } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ResultsPanel from "@/components/ResultsPanel";
import { listCalcs, deleteCalc } from "@/lib/storage";

export default function History() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = () => setItems(listCalcs());

  useEffect(() => { load(); }, []);

  const del = (id) => {
    if (!window.confirm("Eliminare la simulazione?")) return;
    deleteCalc(id);
    toast.success("Simulazione eliminata");
    load();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold text-ink tracking-tight" data-testid="history-title">Storico</h1>
          <p className="mt-2 text-ink-muted">Le tue simulazioni salvate localmente nel browser.</p>
        </div>
        <Link
          to="/calcolatore"
          className="hidden md:inline-flex px-4 h-10 items-center bg-brand text-bg text-sm font-medium rounded-md hover:-translate-y-[1px]"
        >
          Nuova simulazione
        </Link>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-line rounded-lg p-10 text-center text-ink-muted" data-testid="history-empty">
          Nessuna simulazione salvata. <Link to="/calcolatore" className="text-brand underline">Vai al calcolatore</Link>.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <div key={it.id} className="bg-white border border-line rounded-lg p-5 shadow-card hover:shadow-card-hover transition" data-testid={`calc-item-${it.id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="font-display font-semibold text-ink truncate">{it.nome}</div>
                  <div className="mt-1 text-[11px] text-ink-muted font-mono flex items-center gap-1.5">
                    <Calendar className="w-3 h-3" />
                    {new Date(it.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}
                  </div>
                </div>
                <span className="text-[10px] font-mono uppercase tracking-wider bg-brand/10 text-brand px-2 py-0.5 rounded">
                  {it.result.input.tipologia === "industria_4_0" ? "4.0" : "Transizione"}
                </span>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Investimento</div>
                  <div className="font-mono text-sm text-ink">{fmtEUR(it.result.input.investimento)}</div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Vantaggio</div>
                  <div className="font-mono text-sm text-brand font-semibold">{fmtEUR(it.result.vantaggio_complessivo)}</div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-line flex items-center justify-between">
                <button
                  onClick={() => setSelected(it)}
                  data-testid={`view-${it.id}`}
                  className="inline-flex items-center gap-1.5 text-sm text-brand hover:text-brand-dark"
                >
                  <Eye className="w-3.5 h-3.5" /> Dettaglio
                </button>
                <button
                  onClick={() => del(it.id)}
                  data-testid={`delete-${it.id}`}
                  className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-red-600"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{selected?.nome}</DialogTitle>
          </DialogHeader>
          {selected && <ResultsPanel result={selected.result} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
